import { Mark, Node, mergeAttributes } from '@tiptap/core'
import { Fragment } from '@tiptap/pm/model'
import { Plugin, PluginKey, TextSelection } from 'prosemirror-state'
import { Decoration, DecorationSet } from 'prosemirror-view'
import { MentionPluginKey, hasVisibleMentionSuggestionPopup } from './mentionPluginKey'
import { rangeIsInsideCodeLiteral } from './codeLiteralContext'

export const ACTION_PROMPT_SENTINEL = '\u00a0'
export const ACTION_PROMPT_PLACEHOLDER = 'Prompt'
const ACTION_LABEL_RENDER_ATTR = 'attr'

interface ActionTokenPromptChangePayload {
    attrs: Record<string, unknown>
    index: number
    promptContent: string
    committed?: boolean
    removed?: boolean
}

interface ActionTokenExtensionOptions {
    onPromptContentChange?: (payload: ActionTokenPromptChangePayload) => void
    readOnlyPrompts?: boolean
}

const stripPromptSentinel = (value: string | null | undefined): string =>
    String(value || '')
        .replace(new RegExp(ACTION_PROMPT_SENTINEL, 'g'), '')
        .replace(/\u200b/g, '')

const normalizePromptText = (value: string | null | undefined): string =>
    stripPromptSentinel(value)
        .replace(/\u00a0/g, ' ')
        .replace(/[\r\n]+/g, ' ')
        .trim()

const editorPromptText = (value: string | null | undefined): string =>
    stripPromptSentinel(value)
        .replace(/\u00a0/g, ' ')
        .replace(/[\r\n]+/g, ' ')

const supportsPromptInput = (attrs: Record<string, unknown> | null | undefined): boolean =>
    attrs?.supportsPromptInput !== false

export const isAndroidActionPromptInputUserAgent = (
    userAgent: string | null | undefined,
): boolean => /\bAndroid\b/i.test(String(userAgent || ''))

const shouldUseNativeAndroidPromptInput = (): boolean =>
    typeof navigator !== 'undefined' && isAndroidActionPromptInputUserAgent(navigator.userAgent)

const actionNodePromptContent = (node: { textContent?: string; attrs?: Record<string, unknown> }) =>
    normalizePromptText(node.textContent) ||
    (node.attrs?.promptPlaceholder === true
        ? ''
        : normalizePromptText(node.attrs?.promptContent as string))

const TEXT_NODE_TYPE = 3

export const actionPromptSelectionPos = (
    promptMatch: {
        tokenNode: { textContent?: string }
        contentFrom: number
        contentTo: number
    },
    promptOffset: number,
    docContentSize: number,
): number | null => {
    const { tokenNode, contentFrom, contentTo } = promptMatch
    if (
        !Number.isFinite(promptOffset) ||
        !Number.isFinite(contentFrom) ||
        !Number.isFinite(contentTo) ||
        !Number.isFinite(docContentSize) ||
        contentFrom < 0 ||
        contentTo < contentFrom ||
        contentTo > docContentSize
    ) {
        return null
    }

    const promptTextLength = editorPromptText(tokenNode.textContent).length
    const promptContentSize = contentTo - contentFrom
    const maxPromptOffset = Math.max(0, Math.min(promptTextLength, promptContentSize))
    const boundedPromptOffset = Math.max(0, Math.min(promptOffset, maxPromptOffset))

    return contentFrom + boundedPromptOffset
}

export const actionPromptEditorText = (promptContent: string): string =>
    promptContent ? `${promptContent}${ACTION_PROMPT_SENTINEL}` : ACTION_PROMPT_SENTINEL

export const shouldCommitActionPrompt = (
    promptContent: string,
    promptPlaceholder: boolean | null | undefined,
) => !!promptContent || promptPlaceholder !== true

const stopPromptSubmitEvent = (event: Event) => {
    event.preventDefault()
    event.stopPropagation()
    if ('stopImmediatePropagation' in event) {
        event.stopImmediatePropagation()
    }
}

const actionAttrsWithPrompt = (
    attrs: Record<string, unknown>,
    promptContent: string,
): Record<string, unknown> => {
    const nextAttrs: Record<string, unknown> = {
        ...attrs,
        promptContent: promptContent || null,
        textInputValue: promptContent || null,
        hasTextInput: !!promptContent,
        syncWithDraft: false,
    }
    delete nextAttrs.promptPlaceholder
    return nextAttrs
}

const actionTokenAttributes = (options: { promptMark?: boolean } = {}) => ({
    tag: {
        default: null,
        parseHTML: element =>
            element.getAttribute('data-action-tag') ||
            element.getAttribute('data-action-prompt-tag'),
        renderHTML: attributes =>
            attributes.tag
                ? {
                      [options.promptMark ? 'data-action-prompt-tag' : 'data-action-tag']:
                          attributes.tag,
                  }
                : {},
    },
    ai_action_id: {
        default: null,
        parseHTML: element =>
            element.getAttribute('data-ai-action-id') || element.getAttribute('data-action-id'),
        renderHTML: attributes =>
            attributes.ai_action_id
                ? {
                      'data-ai-action-id': attributes.ai_action_id,
                  }
                : {},
    },
    promptContent: {
        default: null,
        parseHTML: element => element.getAttribute('data-action-prompt-content'),
        renderHTML: attributes =>
            attributes.promptContent
                ? { 'data-action-prompt-content': attributes.promptContent }
                : {},
    },
    // Click-time prompt-craft lifecycle ('pending' | 'succeeded' | 'failed'); 'pending'
    // renders the pill's prompt area as a loading state instead of an empty placeholder.
    craftStatus: {
        default: null,
        parseHTML: element => element.getAttribute('data-action-craft-status'),
        renderHTML: attributes =>
            attributes.craftStatus ? { 'data-action-craft-status': attributes.craftStatus } : {},
    },
    displayName: {
        default: null,
        parseHTML: element => element.getAttribute('data-action-display-name'),
        renderHTML: attributes =>
            attributes.displayName ? { 'data-action-display-name': attributes.displayName } : {},
    },
    promptPlacement: {
        default: null,
        parseHTML: element => element.getAttribute('data-action-prompt-placement'),
        renderHTML: attributes =>
            attributes.promptPlacement
                ? { 'data-action-prompt-placement': attributes.promptPlacement }
                : {},
    },
    supportsPromptInput: {
        default: true,
        parseHTML: element => element.getAttribute('data-action-supports-prompt-input') !== 'false',
        renderHTML: attributes =>
            attributes.supportsPromptInput === false
                ? { 'data-action-supports-prompt-input': 'false' }
                : {},
    },
    actionDefinitionId: {
        default: null,
        parseHTML: element =>
            element.getAttribute('data-action-definition-id') ||
            element.getAttribute('data-actiondefinition-id'),
        renderHTML: attributes =>
            attributes.actionDefinitionId
                ? {
                      'data-action-definition-id': attributes.actionDefinitionId,
                  }
                : {},
    },
    kind: {
        default: null,
        parseHTML: element => element.getAttribute('data-action-kind'),
        renderHTML: attributes => (attributes.kind ? { 'data-action-kind': attributes.kind } : {}),
    },
    aiModelId: {
        default: null,
        parseHTML: element => element.getAttribute('data-ai-model-id'),
        renderHTML: attributes =>
            attributes.aiModelId ? { 'data-ai-model-id': attributes.aiModelId } : {},
    },
    toolKey: {
        default: null,
        parseHTML: element => element.getAttribute('data-tool-key'),
        renderHTML: attributes =>
            attributes.toolKey ? { 'data-tool-key': attributes.toolKey } : {},
    },
    analyticsSource: {
        default: null,
        parseHTML: element => element.getAttribute('data-analytics-source'),
        renderHTML: attributes =>
            attributes.analyticsSource
                ? { 'data-analytics-source': attributes.analyticsSource }
                : {},
    },
    promptPlaceholder: {
        default: null,
        parseHTML: element => element.getAttribute('data-action-prompt-placeholder') === 'true',
        renderHTML: attributes =>
            attributes.promptPlaceholder === true
                ? { 'data-action-prompt-placeholder': 'true' }
                : {},
    },
})

// Backward-compatible parser/serializer for old sibling prompt-mark documents.
// New documents store the prompt as text content inside actionTag.
export const ActionPromptMarkExtension = Mark.create({
    name: 'actionPrompt',

    inclusive: true,

    addAttributes() {
        return actionTokenAttributes({ promptMark: true })
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-action-prompt-mark]',
            },
        ]
    },

    renderHTML({ mark, HTMLAttributes }) {
        const tag = mark.attrs.tag ? String(mark.attrs.tag).replace(/^!+/, '') : ''
        const displayName =
            typeof mark.attrs.displayName === 'string' ? mark.attrs.displayName.trim() : ''
        const tagLabel = displayName || (tag ? `!${tag}` : 'AI action')

        return [
            'span',
            mergeAttributes(HTMLAttributes, {
                class: 'action-prompt-mark',
                'data-action-prompt-mark': 'true',
                'data-action-prompt-tag': tag,
                'data-action-label': tagLabel,
            }),
            0,
        ]
    },
})

export const ActionTokenExtension = Node.create<ActionTokenExtensionOptions>({
    name: 'actionTag',

    group: 'inline',
    inline: true,
    content: 'text*',
    marks: '',
    atom: false,
    selectable: false,
    defining: true,

    addOptions() {
        return {
            onPromptContentChange: undefined,
            readOnlyPrompts: false,
        }
    },

    addAttributes() {
        return actionTokenAttributes()
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-action-tag]:not([data-action-prompt-mark])',
                getContent: (element, schema) => {
                    const promptIsPlaceholder =
                        (element as HTMLElement).getAttribute('data-action-prompt-placeholder') ===
                        'true'
                    const promptElement = (element as HTMLElement).querySelector(
                        '.action-token-prompt, .action-tag-prompt',
                    )
                    const promptContent = promptIsPlaceholder
                        ? ''
                        : normalizePromptText(
                              promptElement?.textContent ||
                                  (element as HTMLElement).getAttribute(
                                      'data-action-prompt-content',
                                  ),
                          )
                    const promptEditorText = promptContent
                        ? actionPromptEditorText(promptContent)
                        : (element as HTMLElement).getAttribute(
                              'data-action-supports-prompt-input',
                          ) === 'false'
                        ? ''
                        : ACTION_PROMPT_SENTINEL
                    return promptEditorText
                        ? Fragment.from(schema.text(promptEditorText))
                        : Fragment.empty
                },
            },
        ]
    },

    renderText({ node }) {
        const tag = node.attrs.tag ? String(node.attrs.tag).replace(/^!+/, '') : ''
        const label = tag ? `!${tag}` : 'AI action'
        const promptContent = actionNodePromptContent(node)
        return promptContent ? `${label} ${promptContent}` : label
    },

    renderHTML({ node, HTMLAttributes }) {
        const tag = node.attrs.tag ? String(node.attrs.tag).replace(/^!+/, '') : ''
        const displayName =
            typeof node.attrs.displayName === 'string' ? node.attrs.displayName.trim() : ''
        const tagLabel = displayName || (tag ? `!${tag}` : 'AI action')
        const promptContent = actionNodePromptContent(node)
        const supportsPromptInput = node.attrs.supportsPromptInput !== false
        const readOnlyPrompts = this.options.readOnlyPrompts === true

        const attrs = mergeAttributes(HTMLAttributes, {
            class: 'action-token action-tag',
            'data-action-tag': tag,
            'data-action-label': tagLabel,
            'data-action-supports-prompt-input': supportsPromptInput ? 'true' : 'false',
            ...(readOnlyPrompts ? { 'data-action-prompts-readonly': 'true' } : {}),
            title: promptContent ? `${tagLabel} ${promptContent}` : tagLabel,
        })

        const labelSpec: [string, Record<string, string>] = [
            'span',
            {
                class: 'action-token-label action-tag-label',
                contenteditable: 'false',
                'data-action-label': tagLabel,
                'data-action-label-render': ACTION_LABEL_RENDER_ATTR,
                'aria-label': tagLabel,
            },
        ]

        if (!supportsPromptInput || (readOnlyPrompts && !promptContent)) {
            return ['span', attrs, labelSpec]
        }

        const craftPending = node.attrs.craftStatus === 'pending' && !promptContent

        return [
            'span',
            attrs,
            labelSpec,
            [
                'span',
                {
                    class: 'action-token-prompt action-tag-prompt',
                    'data-action-token-prompt-content': 'true',
                    'data-action-token-empty-prompt': promptContent ? 'false' : 'true',
                    ...(craftPending ? { 'data-action-token-crafting': 'true' } : {}),
                    ...(readOnlyPrompts
                        ? {
                              'data-action-prompt-content': promptContent,
                          }
                        : {
                              'data-placeholder': craftPending
                                  ? 'Crafting prompt…'
                                  : ACTION_PROMPT_PLACEHOLDER,
                          }),
                },
                0,
            ],
        ]
    },

    addProseMirrorPlugins() {
        const actionNodeName = this.name
        const onPromptContentChange = this.options.onPromptContentChange
        const readOnlyPrompts = this.options.readOnlyPrompts === true

        const actionTokenIndexAtPos = (doc, pos: number) => {
            let tokenIndex = 0
            let foundIndex = -1

            doc.descendants((node, nodePos) => {
                if (node.type.name !== actionNodeName) return true
                if (nodePos === pos) {
                    foundIndex = tokenIndex
                    return false
                }
                tokenIndex += 1
                return false
            })

            return foundIndex
        }

        const actionTokenAtSelection = state => {
            const { from, to } = state.selection
            let match = null

            state.doc.descendants((node, pos) => {
                if (node.type.name !== actionNodeName) return true

                const contentFrom = pos + 1
                const contentTo = pos + node.nodeSize - 1
                if (from >= contentFrom && to <= contentTo) {
                    match = { tokenNode: node, tokenPos: pos, contentFrom, contentTo }
                    return false
                }
                return false
            })

            return match
        }

        const actionTokenBeforeSelection = state => {
            const { selection } = state
            if (!selection.empty) return null

            const caretPos = selection.from
            let match = null

            state.doc.descendants((node, pos) => {
                if (match) return false
                if (node.type.name !== actionNodeName) return true

                const afterToken = pos + node.nodeSize
                const nextChar = state.doc.textBetween(
                    afterToken,
                    Math.min(afterToken + 1, state.doc.content.size),
                    '',
                    '',
                )
                const afterBoundary = nextChar === ' ' ? afterToken + 1 : afterToken
                if (caretPos === afterToken || caretPos === afterBoundary) {
                    match = {
                        tokenNode: node,
                        tokenPos: pos,
                        contentFrom: pos + 1,
                        contentTo: pos + node.nodeSize - 1,
                    }
                    return false
                }
                return false
            })

            return match
        }

        const actionTokenPromptSnapshots = doc => {
            const snapshots: Array<{
                actionId: string
                definitionId: string
                tag: string
                promptContent: string
            }> = []

            doc.descendants(node => {
                if (node.type.name !== actionNodeName) return true

                snapshots.push({
                    actionId: String(node.attrs.ai_action_id || ''),
                    definitionId: String(node.attrs.actionDefinitionId || ''),
                    tag: String(node.attrs.tag || '').replace(/^!+/, ''),
                    promptContent: normalizePromptText(node.textContent),
                })
                return false
            })

            return snapshots
        }

        const sameActionTokenIdentity = (left, right): boolean => {
            if (left.actionId || right.actionId) return left.actionId === right.actionId
            if (left.definitionId || right.definitionId)
                return left.definitionId === right.definitionId
            return left.tag === right.tag
        }

        const changesExistingActionPromptContent = (oldDoc, newDoc): boolean => {
            const oldSnapshots = actionTokenPromptSnapshots(oldDoc)
            const newSnapshots = actionTokenPromptSnapshots(newDoc)

            if (oldSnapshots.length !== newSnapshots.length) return false

            return oldSnapshots.some((oldSnapshot, index) => {
                const newSnapshot = newSnapshots[index]
                if (!newSnapshot || !sameActionTokenIdentity(oldSnapshot, newSnapshot)) {
                    return false
                }
                return oldSnapshot.promptContent !== newSnapshot.promptContent
            })
        }

        const actionTokenPromptDecorations = state => {
            const { from, to } = state.selection
            const decorations: Decoration[] = []

            state.doc.descendants((node, pos) => {
                if (node.type.name !== actionNodeName) return true
                if (!supportsPromptInput(node.attrs)) return false

                const contentFrom = pos + 1
                const contentTo = pos + node.nodeSize - 1
                const selectionInsidePrompt = from >= contentFrom && to <= contentTo
                if (selectionInsidePrompt) {
                    decorations.push(
                        Decoration.node(pos, pos + node.nodeSize, {
                            'data-action-token-prompt-active': 'true',
                        }),
                    )
                    return false
                }

                return false
            })

            return DecorationSet.create(state.doc, decorations)
        }

        const notifyPromptChange = (
            state,
            pos: number,
            attrs,
            promptContent: string,
            options: { committed?: boolean; removed?: boolean } = {},
        ) => {
            onPromptContentChange?.({
                attrs: actionAttrsWithPrompt(attrs, promptContent),
                index: actionTokenIndexAtPos(state.doc, pos),
                promptContent,
                committed: options.committed === true,
                removed: options.removed === true,
            })
        }

        const posAfterToken = (doc, tokenPos: number, tokenNode): number => {
            const nextPos = tokenPos + tokenNode.nodeSize
            const nextChar = doc.textBetween(
                nextPos,
                Math.min(nextPos + 1, doc.content.size),
                '',
                '',
            )
            return nextChar === ' ' ? nextPos + 1 : nextPos
        }

        const isTextblockEnd = (doc, pos: number): boolean => {
            if (pos < 0 || pos > doc.content.size) return false
            const $pos = doc.resolve(pos)
            return $pos.parent.isTextblock && $pos.parentOffset === $pos.parent.content.size
        }

        const ensureSpaceAfterToken = (tr, tokenPos: number, tokenNode) => {
            const afterToken = tokenPos + tokenNode.nodeSize
            const nextChar = tr.doc.textBetween(
                afterToken,
                Math.min(afterToken + 1, tr.doc.content.size),
                '',
                '',
            )
            if (nextChar === ' ') {
                return { tr, targetPos: afterToken + 1, inserted: false }
            }
            if (!isTextblockEnd(tr.doc, afterToken)) {
                return { tr, targetPos: afterToken, inserted: false }
            }

            tr.insertText(' ', afterToken)
            return { tr, targetPos: afterToken + 1, inserted: true }
        }

        const removeEmptyPromptToken = (view, promptMatch) => {
            notifyPromptChange(view.state, promptMatch.tokenPos, promptMatch.tokenNode.attrs, '', {
                committed: true,
                removed: true,
            })
            if (!onPromptContentChange) {
                const deleteTo = posAfterToken(
                    view.state.doc,
                    promptMatch.tokenPos,
                    promptMatch.tokenNode,
                )
                view.dispatch(view.state.tr.delete(promptMatch.tokenPos, deleteTo))
                view.focus()
            }
        }

        const moveSelectionToPromptEnd = (view, promptMatch) => {
            view.dispatch(
                view.state.tr.setSelection(
                    TextSelection.create(
                        view.state.doc,
                        promptMatch.contentFrom +
                            editorPromptText(promptMatch.tokenNode.textContent).length,
                    ),
                ),
            )
        }

        const insertFirstPromptText = (view, text: string): boolean => {
            const promptMatch = actionTokenAtSelection(view.state)
            if (!promptMatch) return false
            if (normalizePromptText(promptMatch.tokenNode.textContent)) return false

            const nextPromptContent = normalizePromptText(text)
            if (!nextPromptContent) return true

            const nextEditorText = `${nextPromptContent}${ACTION_PROMPT_SENTINEL}`
            let tr = view.state.tr.insertText(
                nextEditorText,
                promptMatch.contentFrom,
                promptMatch.contentTo,
            )
            tr = tr
                .setSelection(
                    TextSelection.create(
                        tr.doc,
                        promptMatch.contentFrom + nextPromptContent.length,
                    ),
                )
                .scrollIntoView()
            view.dispatch(tr)
            view.focus()
            return true
        }

        const actionTokenAtDom = (view, tokenElement: HTMLElement) => {
            let match = null

            view.state.doc.descendants((node, pos) => {
                if (node.type.name !== actionNodeName) return true

                const nodeDom = view.nodeDOM(pos)
                if (nodeDom === tokenElement) {
                    match = {
                        tokenNode: node,
                        tokenPos: pos,
                        contentFrom: pos + 1,
                        contentTo: pos + node.nodeSize - 1,
                    }
                    return false
                }
                return false
            })

            return match
        }

        const eventPoint = (event: Event): { x: number; y: number } | null => {
            if (event instanceof MouseEvent) {
                return { x: event.clientX, y: event.clientY }
            }

            if (event instanceof TouchEvent) {
                const touch = event.touches[0] || event.changedTouches[0]
                return touch ? { x: touch.clientX, y: touch.clientY } : null
            }

            return null
        }

        const textNodeForPrompt = (promptElement: HTMLElement): Text | null => {
            const childNodes = Array.from(promptElement.childNodes)
            const textNode = childNodes.find(node => node.nodeType === TEXT_NODE_TYPE)
            return textNode instanceof Text ? textNode : null
        }

        const nativePromptOffsetFromPoint = (
            promptElement: HTMLElement,
            point: { x: number; y: number },
        ): number | null => {
            const ownerDocument = promptElement.ownerDocument as Document & {
                caretPositionFromPoint?: (
                    x: number,
                    y: number,
                ) => {
                    offsetNode: globalThis.Node
                    offset: number
                } | null
                caretRangeFromPoint?: (x: number, y: number) => Range | null
            }
            const caretPosition = ownerDocument.caretPositionFromPoint?.(point.x, point.y)
            if (
                caretPosition?.offsetNode?.nodeType === TEXT_NODE_TYPE &&
                promptElement.contains(caretPosition.offsetNode)
            ) {
                return caretPosition.offset
            }

            const caretRange = ownerDocument.caretRangeFromPoint?.(point.x, point.y)
            if (
                caretRange?.startContainer?.nodeType === TEXT_NODE_TYPE &&
                promptElement.contains(caretRange.startContainer)
            ) {
                return caretRange.startOffset
            }

            return null
        }

        const promptOffsetFromPoint = (promptElement: HTMLElement, event: Event): number => {
            const textNode = textNodeForPrompt(promptElement)
            const text = textNode?.textContent || ''
            const maxOffset = editorPromptText(text).length
            const point = eventPoint(event)
            if (!point || !textNode || maxOffset <= 0) return maxOffset

            const nativeOffset = nativePromptOffsetFromPoint(promptElement, point)
            if (nativeOffset !== null) {
                return Math.max(0, Math.min(nativeOffset, maxOffset))
            }

            let bestOffset = maxOffset
            let bestDistance = Number.POSITIVE_INFINITY
            for (let offset = 0; offset <= maxOffset; offset += 1) {
                const range = promptElement.ownerDocument.createRange()
                range.setStart(textNode, offset)
                range.setEnd(textNode, offset)
                const rect = range.getClientRects()[0] || range.getBoundingClientRect()
                range.detach?.()
                if (!rect) continue

                const xDistance = Math.abs(point.x - rect.left)
                const yDistance =
                    point.y < rect.top
                        ? rect.top - point.y
                        : point.y > rect.bottom
                        ? point.y - rect.bottom
                        : 0
                const distance = xDistance + yDistance
                if (distance < bestDistance) {
                    bestDistance = distance
                    bestOffset = offset
                }
            }

            return bestOffset
        }

        const selectPromptFromDomEvent = (view, event: Event): boolean => {
            const target = event.target
            const targetElement =
                target instanceof Element
                    ? target
                    : target instanceof Text
                    ? target.parentElement
                    : null
            if (!targetElement) return false

            let promptElement = targetElement.closest('.action-token-prompt, .action-tag-prompt')
            if (!(promptElement instanceof HTMLElement)) {
                const tokenCandidate = targetElement.closest(
                    '.action-token[data-action-tag], .action-tag[data-action-tag]',
                )
                const promptCandidate = tokenCandidate?.querySelector(
                    '.action-token-prompt, .action-tag-prompt',
                )
                const point = eventPoint(event)
                if (promptCandidate instanceof HTMLElement && point) {
                    const rect = promptCandidate.getBoundingClientRect()
                    const isInsidePromptRect =
                        point.x >= rect.left - 4 &&
                        point.x <= rect.right + 4 &&
                        point.y >= rect.top - 4 &&
                        point.y <= rect.bottom + 4
                    if (isInsidePromptRect) {
                        promptElement = promptCandidate
                    }
                }
            }

            if (!(promptElement instanceof HTMLElement) || !view.dom.contains(promptElement)) {
                return false
            }

            const tokenElement = promptElement.closest(
                '.action-token[data-action-tag], .action-tag[data-action-tag]',
            )
            if (!(tokenElement instanceof HTMLElement)) return false
            if (tokenElement.getAttribute('data-action-supports-prompt-input') === 'false') {
                return false
            }

            const promptMatch = actionTokenAtDom(view, tokenElement)
            if (!promptMatch) return false

            const promptOffset = promptOffsetFromPoint(promptElement, event)
            const targetPos = actionPromptSelectionPos(
                promptMatch,
                promptOffset,
                view.state.doc.content.size,
            )
            stopPromptSubmitEvent(event)
            if (targetPos === null) return true

            view.dom.focus({ preventScroll: true })
            if (!view.state.selection.empty || view.state.selection.from !== targetPos) {
                try {
                    view.dispatch(
                        view.state.tr.setSelection(TextSelection.create(view.state.doc, targetPos)),
                    )
                } catch {
                    return true
                }
            }
            return true
        }

        return [
            new Plugin({
                key: new PluginKey('actionToken'),
                props: {
                    decorations: actionTokenPromptDecorations,
                    handleDOMEvents: readOnlyPrompts
                        ? {
                              beforeinput(view, event) {
                                  if (!actionTokenAtSelection(view.state)) return false
                                  stopPromptSubmitEvent(event)
                                  return true
                              },
                              paste(view, event) {
                                  if (!actionTokenAtSelection(view.state)) return false
                                  stopPromptSubmitEvent(event)
                                  return true
                              },
                              drop(view, event) {
                                  if (!actionTokenAtSelection(view.state)) return false
                                  stopPromptSubmitEvent(event)
                                  return true
                              },
                          }
                        : {
                              beforeinput(view, event) {
                                  const inputEvent = event as InputEvent
                                  if (
                                      /insert(LineBreak|Paragraph)/.test(inputEvent.inputType || '')
                                  ) {
                                      const promptMatch = actionTokenAtSelection(view.state)
                                      if (!promptMatch) return false
                                      stopPromptSubmitEvent(event)
                                      return true
                                  }

                                  if (
                                      inputEvent.inputType !== 'insertText' &&
                                      inputEvent.inputType !== 'insertCompositionText'
                                  ) {
                                      return false
                                  }
                                  if (typeof inputEvent.data !== 'string') return false

                                  const promptMatch = actionTokenAtSelection(view.state)
                                  if (
                                      promptMatch &&
                                      shouldUseNativeAndroidPromptInput() &&
                                      !normalizePromptText(promptMatch.tokenNode.textContent)
                                  ) {
                                      return false
                                  }

                                  const handled = insertFirstPromptText(view, inputEvent.data)
                                  if (handled) {
                                      stopPromptSubmitEvent(event)
                                  }
                                  return handled
                              },
                              pointerdown: selectPromptFromDomEvent,
                              touchstart: selectPromptFromDomEvent,
                          },
                    handleKeyDown(view, event) {
                        if (readOnlyPrompts) {
                            if (!actionTokenAtSelection(view.state)) return false
                            if (
                                event.key.length === 1 ||
                                event.key === 'Backspace' ||
                                event.key === 'Delete' ||
                                event.key === 'Enter'
                            ) {
                                stopPromptSubmitEvent(event)
                                return true
                            }
                            return false
                        }
                        const promptMatch = actionTokenAtSelection(view.state)
                        if (!promptMatch) {
                            if (event.key !== 'Backspace') return false

                            const boundaryMatch = actionTokenBeforeSelection(view.state)
                            if (
                                !boundaryMatch ||
                                !supportsPromptInput(boundaryMatch.tokenNode.attrs)
                            ) {
                                return false
                            }

                            const promptContent = normalizePromptText(
                                boundaryMatch.tokenNode.textContent,
                            )
                            stopPromptSubmitEvent(event)

                            if (!promptContent) {
                                removeEmptyPromptToken(view, boundaryMatch)
                                return true
                            }

                            moveSelectionToPromptEnd(view, boundaryMatch)
                            return true
                        }

                        const promptContent = normalizePromptText(promptMatch.tokenNode.textContent)
                        const mentionSuggestionOpen =
                            MentionPluginKey.getState(view.state)?.active === true &&
                            hasVisibleMentionSuggestionPopup(view.dom.ownerDocument)

                        if (
                            mentionSuggestionOpen &&
                            (event.key === 'Enter' || event.key === 'Tab' || event.key === 'Escape')
                        ) {
                            return false
                        }

                        const exitsEmptyPromptWithSpace = event.key === ' ' && !promptContent
                        if (
                            event.key === 'Enter' ||
                            event.key === 'Tab' ||
                            event.key === 'Escape' ||
                            exitsEmptyPromptWithSpace
                        ) {
                            stopPromptSubmitEvent(event)
                            if (
                                shouldCommitActionPrompt(
                                    promptContent,
                                    promptMatch.tokenNode.attrs.promptPlaceholder,
                                )
                            ) {
                                notifyPromptChange(
                                    view.state,
                                    promptMatch.tokenPos,
                                    promptMatch.tokenNode.attrs,
                                    promptContent,
                                    { committed: true },
                                )
                            }
                            let tr = view.state.tr
                            const spaceResult = ensureSpaceAfterToken(
                                tr,
                                promptMatch.tokenPos,
                                promptMatch.tokenNode,
                            )
                            tr = spaceResult.tr
                            view.dispatch(
                                tr
                                    .setSelection(
                                        TextSelection.create(tr.doc, spaceResult.targetPos),
                                    )
                                    .scrollIntoView(),
                            )
                            view.focus()
                            return true
                        }

                        if (
                            (event.key === 'Backspace' || event.key === 'Delete') &&
                            !promptContent
                        ) {
                            stopPromptSubmitEvent(event)
                            removeEmptyPromptToken(view, promptMatch)
                            return true
                        }

                        return false
                    },
                    handleTextInput(view, from, _to, text) {
                        if (readOnlyPrompts) {
                            return !!actionTokenAtSelection(view.state)
                        }
                        const promptMatch = actionTokenAtSelection(view.state)
                        if (!promptMatch) return false

                        if (/[\r\n]/.test(text)) {
                            return true
                        }

                        const incomingText = text.replace(/\u00a0/g, ' ')
                        const currentPromptContent = normalizePromptText(
                            promptMatch.tokenNode.textContent,
                        )

                        if (!currentPromptContent) {
                            if (shouldUseNativeAndroidPromptInput()) {
                                return false
                            }
                            return insertFirstPromptText(view, incomingText)
                        }

                        if (!incomingText.endsWith(' ')) return false

                        const textBeforeCursor = view.state.doc.textBetween(
                            promptMatch.contentFrom,
                            from,
                            '',
                            '',
                        )
                        const textThroughInput = `${textBeforeCursor.replace(
                            /\u00a0/g,
                            ' ',
                        )}${incomingText}`
                        if (!/[ ]{2}$/.test(textThroughInput)) {
                            return false
                        }
                        if (_to < promptMatch.contentTo) {
                            return false
                        }

                        const promptContent = normalizePromptText(
                            textThroughInput.replace(/[ ]{2}$/, ''),
                        )
                        let tr = view.state.tr
                        const nextEditorText = promptContent
                            ? actionPromptEditorText(promptContent)
                            : supportsPromptInput(promptMatch.tokenNode.attrs)
                            ? ACTION_PROMPT_SENTINEL
                            : ''
                        if (nextEditorText) {
                            tr = tr.replaceWith(
                                promptMatch.contentFrom,
                                promptMatch.contentTo,
                                view.state.schema.text(nextEditorText),
                            )
                        } else if (promptMatch.contentTo > promptMatch.contentFrom) {
                            tr = tr.delete(promptMatch.contentFrom, promptMatch.contentTo)
                        }
                        const mappedTokenPos = tr.mapping.map(promptMatch.tokenPos)
                        const mappedToken = tr.doc.nodeAt(mappedTokenPos)
                        if (!mappedToken) return false

                        const afterToken = mappedTokenPos + mappedToken.nodeSize
                        const nextChar = tr.doc.textBetween(
                            afterToken,
                            Math.min(afterToken + 1, tr.doc.content.size),
                            '',
                            '',
                        )
                        let targetPos = afterToken
                        if (nextChar === ' ') {
                            targetPos += 1
                        } else {
                            tr = tr.insertText(' ', afterToken)
                            targetPos += 1
                        }
                        tr = tr
                            .setSelection(TextSelection.create(tr.doc, targetPos))
                            .scrollIntoView()
                        view.dispatch(tr)
                        notifyPromptChange(
                            view.state,
                            mappedTokenPos,
                            promptMatch.tokenNode.attrs,
                            promptContent,
                            { committed: true },
                        )
                        view.focus()
                        return true
                    },
                },
                filterTransaction: (transaction, state) => {
                    if (!readOnlyPrompts || !transaction.docChanged) return true
                    return !changesExistingActionPromptContent(state.doc, transaction.doc)
                },
                appendTransaction: (transactions, _oldState, newState) => {
                    if (readOnlyPrompts) return null
                    if (!transactions.some(transaction => transaction.docChanged)) {
                        return null
                    }

                    const { doc, schema } = newState
                    let tr = null
                    const promptChangePayloads: ActionTokenPromptChangePayload[] = []
                    let actionTokenIndex = 0

                    doc.descendants((node, pos) => {
                        if (node.type.name !== actionNodeName) return true

                        const contentFrom = pos + 1
                        const contentTo = pos + node.nodeSize - 1
                        const afterToken = pos + node.nodeSize
                        const rawNodeText = String(node.textContent || '')
                        const hasPromptSentinel = rawNodeText.includes(ACTION_PROMPT_SENTINEL)
                        const rawEditorText = rawNodeText
                            .replace(/\u00a0/g, ' ')
                            .replace(/[\r\n]+/g, ' ')
                        const rawPromptContent = editorPromptText(node.textContent)
                        const promptContent = normalizePromptText(rawPromptContent)
                        const currentPromptContent =
                            node.attrs.promptPlaceholder === true
                                ? ''
                                : normalizePromptText(node.attrs.promptContent)
                        const selectionInsidePrompt =
                            newState.selection.empty &&
                            newState.selection.from >= contentFrom &&
                            newState.selection.from <= contentTo
                        const selectionAtPromptEnd =
                            selectionInsidePrompt && newState.selection.from === contentTo
                        const selectionAtContentBoundary =
                            newState.selection.empty &&
                            newState.selection.from >= contentTo &&
                            newState.selection.from <= afterToken
                        const nextChar = doc.textBetween(
                            afterToken,
                            Math.min(afterToken + 1, doc.content.size),
                            '',
                            '',
                        )
                        const missingBoundarySpace =
                            supportsPromptInput(node.attrs) &&
                            nextChar !== ' ' &&
                            isTextblockEnd(doc, afterToken)

                        if (/[ ]{2}$/.test(rawPromptContent) && selectionAtPromptEnd) {
                            if (!tr) tr = newState.tr
                            const mappedContentFrom = tr.mapping.map(contentFrom)
                            const mappedContentTo = tr.mapping.map(contentTo)
                            const nextEditorText = promptContent
                                ? actionPromptEditorText(promptContent)
                                : supportsPromptInput(node.attrs)
                                ? ACTION_PROMPT_SENTINEL
                                : ''
                            if (nextEditorText) {
                                tr.replaceWith(
                                    mappedContentFrom,
                                    mappedContentTo,
                                    schema.text(nextEditorText),
                                )
                            } else if (mappedContentTo > mappedContentFrom) {
                                tr.delete(mappedContentFrom, mappedContentTo)
                            }
                            const mappedTokenPos = tr.mapping.map(pos)
                            const attrs = actionAttrsWithPrompt(node.attrs, promptContent)
                            tr.setNodeMarkup(mappedTokenPos, undefined, attrs)
                            const mappedToken = tr.doc.nodeAt(mappedTokenPos)
                            if (mappedToken) {
                                const spaceResult = ensureSpaceAfterToken(
                                    tr,
                                    mappedTokenPos,
                                    mappedToken,
                                )
                                tr = spaceResult.tr
                                tr.setSelection(TextSelection.create(tr.doc, spaceResult.targetPos))
                            }
                            promptChangePayloads.push({
                                attrs,
                                index: actionTokenIndex,
                                promptContent,
                                committed: true,
                            })
                            actionTokenIndex += 1
                            return false
                        }

                        if (missingBoundarySpace) {
                            if (!tr) tr = newState.tr
                            const mappedTokenPos = tr.mapping.map(pos)
                            const mappedToken = tr.doc.nodeAt(mappedTokenPos)
                            if (mappedToken?.type.name === actionNodeName) {
                                const spaceResult = ensureSpaceAfterToken(
                                    tr,
                                    mappedTokenPos,
                                    mappedToken,
                                )
                                tr = spaceResult.tr
                                if (
                                    spaceResult.inserted &&
                                    newState.selection.empty &&
                                    newState.selection.from >= contentTo &&
                                    newState.selection.from <= afterToken
                                ) {
                                    tr.setSelection(
                                        TextSelection.create(tr.doc, spaceResult.targetPos),
                                    )
                                }
                            }
                        }

                        if (
                            !promptContent &&
                            supportsPromptInput(node.attrs) &&
                            !hasPromptSentinel
                        ) {
                            if (!tr) tr = newState.tr
                            const mappedContentFrom = tr.mapping.map(contentFrom)
                            const mappedContentTo = tr.mapping.map(contentTo)
                            const attrs = actionAttrsWithPrompt(node.attrs, '')
                            if (mappedContentTo > mappedContentFrom) {
                                tr.replaceWith(
                                    mappedContentFrom,
                                    mappedContentTo,
                                    schema.text(ACTION_PROMPT_SENTINEL),
                                )
                            } else {
                                tr.insertText(ACTION_PROMPT_SENTINEL, mappedContentFrom)
                            }
                            tr.setNodeMarkup(tr.mapping.map(pos), undefined, attrs)
                            if (selectionInsidePrompt) {
                                tr.setSelection(TextSelection.create(tr.doc, mappedContentFrom))
                            }
                            actionTokenIndex += 1
                            return false
                        }

                        if (promptContent && selectionInsidePrompt && !hasPromptSentinel) {
                            if (!tr) tr = newState.tr
                            const mappedContentFrom = tr.mapping.map(contentFrom)
                            const mappedContentTo = tr.mapping.map(contentTo)
                            const attrs = actionAttrsWithPrompt(node.attrs, promptContent)
                            tr.replaceWith(
                                mappedContentFrom,
                                mappedContentTo,
                                schema.text(`${promptContent}${ACTION_PROMPT_SENTINEL}`),
                            )
                            tr.setNodeMarkup(tr.mapping.map(pos), undefined, attrs)
                            tr.setSelection(
                                TextSelection.create(
                                    tr.doc,
                                    mappedContentFrom + promptContent.length,
                                ),
                            )
                            promptChangePayloads.push({
                                attrs,
                                index: actionTokenIndex,
                                promptContent,
                                committed: false,
                            })
                            actionTokenIndex += 1
                            return false
                        }

                        if (promptContent !== currentPromptContent) {
                            if (!tr) tr = newState.tr
                            const attrs = actionAttrsWithPrompt(node.attrs, promptContent)
                            tr.setNodeMarkup(tr.mapping.map(pos), undefined, attrs)
                            promptChangePayloads.push({
                                attrs,
                                index: actionTokenIndex,
                                promptContent,
                                committed: false,
                            })
                        }

                        actionTokenIndex += 1
                        return false
                    })

                    if (promptChangePayloads.length && onPromptContentChange) {
                        const notifyPromptChanges = () => {
                            promptChangePayloads.forEach(payload => {
                                onPromptContentChange(payload)
                            })
                        }
                        if (typeof globalThis.queueMicrotask === 'function') {
                            globalThis.queueMicrotask(notifyPromptChanges)
                        } else {
                            globalThis.setTimeout(notifyPromptChanges, 0)
                        }
                    }

                    const processedRanges = new Set<string>()

                    doc.descendants((node, pos) => {
                        if (node.type.name === actionNodeName) return false
                        if (!node.isText || !node.text) return

                        const actionCommandRegex = /\B![A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*(?:\.)?/g
                        let match

                        while ((match = actionCommandRegex.exec(node.text)) !== null) {
                            const start = pos + match.index
                            const end = start + match[0].length
                            if (rangeIsInsideCodeLiteral(newState, { from: start, to: end })) {
                                continue
                            }
                            const rangeKey = `${start}-${end}`
                            if (processedRanges.has(rangeKey)) continue
                            processedRanges.add(rangeKey)

                            if (match[0].length > 1) {
                                try {
                                    const $from = doc.resolve(start)
                                    const marks = $from.marks()
                                    const alreadyBold = marks.some(
                                        mark => mark.type.name === 'bold',
                                    )

                                    if (!alreadyBold) {
                                        if (!tr) tr = newState.tr
                                        const boldMark = schema.marks.bold.create()
                                        tr.addMark(
                                            tr.mapping.map(start),
                                            tr.mapping.map(end),
                                            boldMark,
                                        )
                                    }
                                } catch (e) {
                                    console.debug('Error applying bold to action command:', e)
                                }
                            }
                        }
                    })

                    doc.descendants((node, pos) => {
                        if (node.type.name === actionNodeName) return false
                        if (!node.isText || !node.marks.some(mark => mark.type.name === 'bold')) {
                            return
                        }

                        const text = node.text
                        if (!text) return

                        if (
                            rangeIsInsideCodeLiteral(newState, {
                                from: pos,
                                to: pos + node.nodeSize,
                            })
                        ) {
                            return
                        }

                        const actionCommandPattern = /^![A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*(?:\.)?$/
                        if (!actionCommandPattern.test(text)) {
                            const partialPattern = /^![A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*(?:\.)?/
                            const match = text.match(partialPattern)
                            if (match && match[0].length < text.length) {
                                if (!tr) tr = newState.tr
                                const matchEnd = pos + match[0].length
                                const textEnd = pos + text.length
                                const boldMark = node.marks.find(mark => mark.type.name === 'bold')
                                if (boldMark) {
                                    tr.removeMark(
                                        tr.mapping.map(matchEnd),
                                        tr.mapping.map(textEnd),
                                        boldMark,
                                    )
                                }
                            }
                        }
                    })

                    return tr
                },
            }),
        ]
    },
})

export default ActionTokenExtension
